import { useState, useEffect } from 'react';
import { Wrench, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';
import { getRemediationLog, getRemediationRules, toggleRemediationRuleAction, triggerRemediationRuleAction } from '../../../services/firestore/opsService';
import type { RemediationAction, RemediationRule } from '../../../types/ops';

const C = {
  text: '#18181B', textSec: '#52525B', textMuted: '#A1A1AA',
  rose: '#E11D48', pass: '#059669', warn: '#B45309',
  bgCard: '#FFFFFF', border: '#E4E4E7',
};

export function RemediationTab() {
  const [log, setLog] = useState<RemediationAction[]>([]);
  const [rules, setRules] = useState<RemediationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [l, r] = await Promise.all([getRemediationLog(20), getRemediationRules()]);
        setLog(l);
        setRules(r);
      } catch (err) {
        console.error('Failed to load remediation data:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refreshData = async () => {
    const [l, r] = await Promise.all([getRemediationLog(20), getRemediationRules()]);
    setLog(l);
    setRules(r);
  };

  const handleToggle = async (ruleId: string, currentEnabled: boolean) => {
    setActionLoading(ruleId);
    try {
      await toggleRemediationRuleAction(ruleId, !currentEnabled);
      await refreshData();
    } catch (err) {
      console.error('Failed to toggle rule:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleTrigger = async (ruleId: string) => {
    setActionLoading(ruleId);
    try {
      await triggerRemediationRuleAction(ruleId);
      await refreshData();
    } catch (err) {
      console.error('Failed to trigger rule:', err);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: C.textMuted }}>Loading remediation data...</div>;

  return (
    <div>
      {/* Rules config */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20, marginBottom: 16 }}>
        <p style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: C.textMuted, marginBottom: 12 }}>
          Auto-Remediation Rules
        </p>
        {rules.length === 0 ? (
          <p style={{ fontSize: 14, color: C.textSec }}>No rules configured yet. Rules will be seeded when the remediation engine first runs.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Rule</Th>
                <Th>Trigger</Th>
                <Th>Max/Day</Th>
                <Th>Cooldown</Th>
                <Th>Enabled</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 500 }}>{rule.name}</td>
                  <td style={{ padding: '10px 12px', fontSize: 13, color: C.textSec }}>{rule.trigger}</td>
                  <td style={{ padding: '10px 12px', fontSize: 14, textAlign: 'center' }}>{rule.maxAttemptsPerDay}</td>
                  <td style={{ padding: '10px 12px', fontSize: 14, textAlign: 'center' }}>{rule.cooldownMinutes}m</td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleToggle(rule.id, rule.enabled)}
                      disabled={actionLoading === rule.id}
                      style={{
                        display: 'inline-block', width: 36, height: 20, borderRadius: 10,
                        background: rule.enabled ? C.pass : '#D4D4D8',
                        position: 'relative', border: 'none', cursor: 'pointer',
                        opacity: actionLoading === rule.id ? 0.5 : 1,
                      }}
                    >
                      <span style={{
                        display: 'block', width: 16, height: 16, borderRadius: '50%',
                        background: '#fff', position: 'absolute', top: 2,
                        left: rule.enabled ? 18 : 2,
                        transition: 'left 0.2s',
                      }} />
                    </button>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleTrigger(rule.id)}
                      disabled={actionLoading === rule.id || !rule.enabled}
                      style={{
                        fontSize: 12, padding: '4px 10px', borderRadius: 4,
                        border: `1px solid ${C.border}`, background: C.bgCard,
                        color: rule.enabled ? C.rose : C.textMuted,
                        cursor: rule.enabled ? 'pointer' : 'not-allowed',
                        fontWeight: 600,
                        opacity: actionLoading === rule.id ? 0.5 : 1,
                      }}
                    >
                      Run
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Action log */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20 }}>
        <p style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: C.textMuted, marginBottom: 12 }}>
          Recent Auto-Fix Log
        </p>
        {log.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Wrench size={24} style={{ color: C.textMuted, marginBottom: 8 }} />
            <p style={{ fontSize: 14, color: C.textSec }}>No remediation actions yet.</p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Time</Th>
                <Th>Rule</Th>
                <Th>Action</Th>
                <Th>Result</Th>
              </tr>
            </thead>
            <tbody>
              {log.map((entry) => (
                <tr key={entry.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '10px 12px', fontSize: 13, fontFamily: "'JetBrains Mono', monospace", color: C.textMuted }}>
                    {new Date(entry.executedAt).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 14 }}>{entry.ruleId}</td>
                  <td style={{ padding: '10px 12px', fontSize: 13, color: C.textSec }}>{entry.action}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <ResultBadge result={entry.result} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ResultBadge({ result }: { result: string }) {
  if (result === 'success') return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: C.pass }}>
      <CheckCircle2 size={12} /> Success
    </span>
  );
  if (result === 'failed') return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: C.rose }}>
      <XCircle size={12} /> Failed
    </span>
  );
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: C.textMuted }}>
      <MinusCircle size={12} /> Skipped
    </span>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th style={{
      textAlign: 'left', fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: 0.5, color: '#A1A1AA', padding: '8px 12px', borderBottom: '1px solid #E4E4E7',
    }}>
      {children}
    </th>
  );
}
